/*
 * Copyright (c) 2020. World Wide Generation Ltd
 */

import React from 'react';
import { DropdownItem, DropdownMenu, DropdownToggle, UncontrolledDropdown } from 'reactstrap';
import { Avatar } from '@g17eco/atoms/avatar';
import { Portfolio } from '../../types/portfolio';
import { DATE, formatDate } from '../../utils/date';
import { naturalSort } from '../../utils';
import { MetricGroup, MetricGroupShare } from '../../types/metricGroup';
import { removeCompanyShareFromMetricGroup } from '../../actions/initiative';
import { Loader } from '@g17eco/atoms/loader';
import G17Client from '../../services/G17Client';
import './styles.scss';
import { useSiteAlert } from '../../hooks/useSiteAlert';
import { PACK } from '@constants/terminology';
import { LoadingPlaceholder } from '@g17eco/molecules/loader-container';
import { ColumnDef, Table } from '@g17eco/molecules/table';

interface MenuButtonProps {
  portfolioId: string;
  groupId: string;
  companyId: string;
  disabled?: boolean;
  isReadOnly?: boolean;
  handleReload: (resetView?: boolean) => void;
}

const MenuButton = ({ portfolioId, isReadOnly, groupId, companyId, disabled, handleReload }: MenuButtonProps) => {
  const [isSaving, setSaving] = React.useState(false);
  const { addSiteAlert } = useSiteAlert();

  const handleRemove = async (companyId: string) => {
    setSaving(true);
    try {
      await removeCompanyShareFromMetricGroup(portfolioId, groupId, companyId);
      addSiteAlert({ content: `The ${PACK.SINGULAR} has been removed` });
      handleReload();
    } catch (e) {
      console.log(e);
    }
    setSaving(false);
  };

  if (isSaving) {
    return <Loader />;
  }

  const handleDownloadIndividualData = async () => {
    const url = G17Client.getDownloadUrl(`portfolios/${portfolioId}/metrics/${groupId}/download`);
    window.open(url, '_blank', '');
  };

  return (
    <UncontrolledDropdown>
      <DropdownToggle color='transparent' outline className='px-2'>
        <i className='fas fa-bars' />
      </DropdownToggle>
      <DropdownMenu>
        <DropdownItem disabled={disabled} size='sm' onClick={() => handleDownloadIndividualData()}>
          <i className='fa fa-file-csv mr-2' />
          Download company report for this metric group
        </DropdownItem>
        {!isReadOnly && (
          <>
            <DropdownItem divider />
            <DropdownItem disabled={disabled} size='sm' onClick={() => handleRemove(companyId)}>
              <i className='fa fa-times mr-2 text-ThemeDangerMedium' />
              Remove this company
            </DropdownItem>
          </>
        )}
      </DropdownMenu>
    </UncontrolledDropdown>
  );
};

interface CompanyAssignmentsTableProps {
  metricGroup: MetricGroup;
  handleReload: (resetView?: boolean) => void;
  portfolio: Portfolio;
  isReadOnly?: boolean;
}

const CompanyAssignmentsTable = (props: CompanyAssignmentsTableProps) => {
  const { metricGroup, isReadOnly, handleReload, portfolio } = props;

  const [companies, setCompanies] = React.useState<MetricGroupShare[]>([]);
  const [isLoaded, setLoaded] = React.useState(false);

  const filteredCompanies = companies.filter((c) => !!c.initiativeId);

  React.useEffect(() => {
    const companies = (metricGroup.share ? [...metricGroup.share] : []).sort((a, b) =>
      naturalSort(a.initiative?.name ?? '', b.initiative?.name ?? '')
    );

    setCompanies(companies);
    setLoaded(true);
  }, [metricGroup]);

  if (!metricGroup._id) {
    // Only can do assignments once the metric group exists (no _id means it's not created yet)
    return <></>;
  }

  const tableColumns: ColumnDef<MetricGroupShare>[] = [
    {
      accessorKey: 'name',
      header: () => <span className='ml-4 pl-3'>Company Name</span>,
      meta: {
        cellProps: {
          className: 'nameCol',
        },
      },
      cell: ({ row }) => {
        const data = row.original;
        const company = data.initiative;
        if (!company) {
          console.log('Invalid company: ', data);
          return <></>;
        }

        return (
          <div className={'d-flex align-items-center'}>
            <div className='profile mr-2'>
              <Avatar width='30px'>{company.profile ? <img alt='avatar' src={company.profile} /> : <></>}</Avatar>
            </div>
            <div className='flex-fill'>{company.name ?? ''}</div>
          </div>
        );
      },
    },
    {
      accessorKey: 'acceptedDate',
      header: () => 'Accepted Date',
      accessorFn: (data) => (data.acceptedDate ? formatDate(data.acceptedDate, DATE.HUMANIZE) : 'not accepted'),
    },
    {
      id: 'initiativeId',
      header: () => '',
      meta: {
        cellProps: {
          className: 'buttonsCol',
        },
      },
      cell: ({ row }) => (
        <MenuButton
          isReadOnly={isReadOnly}
          portfolioId={portfolio._id}
          groupId={metricGroup._id ?? ''}
          companyId={row.original.initiativeId}
          handleReload={handleReload}
        />
      ),
    },
  ];

  const noResults = filteredCompanies.length === 0;

  return (
    <>
      <LoadingPlaceholder isLoading={!isLoaded} height={40} />
      <LoadingPlaceholder isLoading={!isLoaded} height={40} count={5} className='mt-1 background-ThemeTextWhite'>
        {noResults ? (
          <div className='alert alert-primary'>This custom metric group has not been shared with any companies</div>
        ) : (
          <div className='portfolio-table-container'>
            <Table
              responsive={true}
              showRowCount={true}
              columns={tableColumns}
              data={filteredCompanies}
              pageSize={25}
            />
          </div>
        )}
      </LoadingPlaceholder>
    </>
  );
};

export default CompanyAssignmentsTable;
